import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu, Loader2,
  LayoutDashboard, BarChart3, Workflow, Activity, Store,
  DollarSign, Star, ChevronRight, Send, ArrowRight, Shield,
  CheckCircle2, Clock, Filter, Plus, ExternalLink, X
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  { value: 'all', label: 'All Industries' },
  { value: 'general', label: 'General Business' },
  { value: 'construction', label: 'Construction' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'realestate', label: 'Real Estate' },
  { value: 'legal', label: 'Legal' },
  { value: 'ecommerce', label: 'E-Commerce' },
  { value: 'marketing', label: 'Marketing' },
];

const Marketplace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tab, setTab] = useState('browse'); // browse, my-listings, inquiries
  const [searchQuery, setSearchQuery] = useState('');
  const [industry, setIndustry] = useState('all');
  const [results, setResults] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [inquiryForm, setInquiryForm] = useState({ company_name: '', contact_name: '', contact_email: '', message: '', budget_range: '' });
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [publishForm, setPublishForm] = useState({ workflow_id: '', service_name: '', service_description: '', outcome_statement: '', price_monthly: 800, industries: [], max_clients: 10, delivery_sla_days: 7 });

  useEffect(() => {
    fetchFeatured();
    fetchStats();
    if (user) {
      fetchMyListings();
      fetchInquiries();
      fetchWorkflows();
    }
  }, []);

  const fetchFeatured = async () => {
    try {
      const res = await axios.get(`${API}/marketplace/featured`);
      setResults(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/marketplace/stats`);
      setStats(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchMyListings = async () => {
    try {
      const res = await axios.get(`${API}/marketplace/my-listings`);
      setMyListings(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchInquiries = async () => {
    try {
      const res = await axios.get(`${API}/marketplace/inquiries`);
      setInquiries(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchWorkflows = async () => {
    try {
      const res = await axios.get(`${API}/workflows/mine`);
      setWorkflows(res.data.filter(w => w.completeness_score >= 90 && w.automation_potential >= 75));
    } catch (err) { console.error(err); }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return fetchFeatured();
    setLoading(true);
    try {
      const payload = { query: searchQuery };
      if (industry !== 'all') payload.industry = industry;
      const res = await axios.post(`${API}/marketplace/search`, payload);
      setResults(res.data.results || []);
      toast.success(`Found ${res.data.total} services`);
    } catch (err) { toast.error('Search failed'); }
    finally { setLoading(false); }
  };

  const handleInquiry = async () => {
    if (!inquiryForm.company_name || !inquiryForm.contact_email || !inquiryForm.message) {
      return toast.error('Fill in company, email, and message');
    }
    try {
      await axios.post(`${API}/marketplace/inquire`, { listing_id: selectedListing.id, ...inquiryForm });
      toast.success('Inquiry sent!');
      setShowInquiryModal(false);
      setInquiryForm({ company_name: '', contact_name: '', contact_email: '', message: '', budget_range: '' });
    } catch (err) { toast.error('Failed to send inquiry'); }
  };

  const handlePublish = async () => {
    if (!publishForm.workflow_id || !publishForm.service_name || !publishForm.outcome_statement) {
      return toast.error('Fill in required fields');
    }
    try {
      await axios.post(`${API}/marketplace/publish`, publishForm);
      toast.success('Published to marketplace!');
      setShowPublishModal(false);
      fetchMyListings();
      fetchFeatured();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    }
  };

  const handleInquiryStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/marketplace/inquiries/${id}/status?status=${status}`);
      toast.success(`Inquiry ${status}`);
      fetchInquiries();
    } catch (err) { toast.error('Failed to update'); }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: BarChart3, label: 'Demand Intel', path: '/demand' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: Activity, label: 'API Hub', path: '/hub' },
  ];

  const getQualityColor = (score) => score >= 90 ? 'text-emerald-400' : score >= 70 ? 'text-amber-400' : 'text-muted-foreground';
  const getStatusStyle = (s) => {
    const map = { new: 'bg-blue-500/20 text-blue-400', reviewed: 'bg-amber-500/20 text-amber-400', responded: 'bg-violet-500/20 text-violet-400', converted: 'bg-emerald-500/20 text-emerald-400', declined: 'bg-red-500/20 text-red-400' };
    return map[s] || 'bg-white/10 text-muted-foreground';
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <HSLogo size={32} />
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}>
                  <Icon className="w-5 h-5" />{item.label}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">{user?.name?.charAt(0) || 'U'}</div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-muted-foreground truncate">{user?.email}</p></div>
            </div>
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              <h1 className="text-lg font-bold" data-testid="marketplace-title">Marketplace</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden">
                {[
                  { key: 'browse', label: 'BROWSE' },
                  { key: 'my-listings', label: 'MY LISTINGS' },
                  { key: 'inquiries', label: 'INQUIRIES' },
                ].map(t => (
                  <button key={t.key} onClick={() => setTab(t.key)}
                    className={`px-3 py-1.5 text-xs font-mono transition-colors ${tab === t.key ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:text-white'}`}
                    data-testid={`tab-${t.key}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <Button onClick={() => setShowPublishModal(true)} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-9 text-sm" data-testid="publish-btn">
                <Plus className="w-4 h-4 mr-1" /> Publish
              </Button>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-6">
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-3 gap-4" data-testid="marketplace-stats">
              <div className="bg-[#0a0a0a] border border-white/5 p-4">
                <p className="text-[10px] text-muted-foreground uppercase">Active Services</p>
                <p className="font-mono text-2xl font-bold text-emerald-400">{stats.total_listings}</p>
              </div>
              <div className="bg-[#0a0a0a] border border-white/5 p-4">
                <p className="text-[10px] text-muted-foreground uppercase">Operators</p>
                <p className="font-mono text-2xl font-bold text-violet-400">{stats.total_operators}</p>
              </div>
              <div className="bg-[#0a0a0a] border border-white/5 p-4">
                <p className="text-[10px] text-muted-foreground uppercase">Inquiries</p>
                <p className="font-mono text-2xl font-bold text-blue-400">{stats.total_inquiries}</p>
              </div>
            </div>
          )}

          {tab === 'browse' && (
            <>
              {/* Search */}
              <form onSubmit={handleSearch} className="bg-[#0a0a0a] border border-white/5 p-4">
                <div className="flex gap-3">
                  <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder='Search by outcome... "AI scheduling", "automated billing"' className="flex-1 bg-black/50 border-white/10 rounded-sm" data-testid="marketplace-search" />
                  <Select value={industry} onValueChange={setIndustry}>
                    <SelectTrigger className="w-40 bg-black/50 border-white/10 rounded-sm" data-testid="marketplace-industry"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10">
                      {INDUSTRIES.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="submit" disabled={loading} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="marketplace-search-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </form>

              {/* Results */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="marketplace-results">
                {results.length > 0 ? results.map(listing => (
                  <div key={listing.id} className="bg-[#0a0a0a] border border-white/5 hover:border-emerald-500/20 transition-all group" data-testid={`listing-${listing.id}`}>
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-bold text-sm leading-tight group-hover:text-emerald-400 transition-colors">{listing.service_name}</h3>
                        <span className={`font-mono text-xs px-1.5 py-0.5 ${getQualityColor(listing.quality_scores?.overall)} bg-white/5`}>
                          {listing.quality_scores?.overall}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{listing.service_description}</p>

                      <div className="bg-emerald-500/5 border border-emerald-500/10 p-2 mb-3">
                        <p className="text-[10px] text-emerald-400 uppercase">Outcome</p>
                        <p className="text-xs text-white">{listing.outcome_statement}</p>
                      </div>

                      <div className="flex items-center justify-between text-xs mb-3">
                        <span className="font-mono text-emerald-400 font-bold">${listing.price_monthly}/mo</span>
                        <span className="text-muted-foreground">{listing.operator_name}</span>
                      </div>

                      <div className="flex flex-wrap gap-1 mb-3">
                        {(listing.industries || []).map(ind => (
                          <span key={ind} className="text-[10px] px-1.5 py-0.5 bg-white/5 text-muted-foreground uppercase">{ind}</span>
                        ))}
                      </div>

                      <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-2 border-t border-white/5">
                        <span>{listing.workflow_snapshot?.task_count || 0} tasks automated</span>
                        <span>SLA: {listing.delivery_sla_days}d</span>
                      </div>
                    </div>
                    <div className="border-t border-white/5 p-3">
                      <Button onClick={() => { setSelectedListing(listing); setShowInquiryModal(true); }} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-8 text-xs" data-testid={`inquire-${listing.id}`}>
                        <Send className="w-3 h-3 mr-1" /> Inquire
                      </Button>
                    </div>
                  </div>
                )) : (
                  <div className="col-span-full bg-[#0a0a0a] border border-white/5 p-12 text-center">
                    <Store className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-lg font-bold mb-2">Marketplace</h3>
                    <p className="text-muted-foreground mb-4 text-sm">Search for AI-augmented services by outcome, or publish your own validated workflow.</p>
                    <Button onClick={() => navigate('/workflow')} variant="outline" className="border-white/10 rounded-sm">Build a Workflow First</Button>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'my-listings' && (
            <div className="space-y-4" data-testid="my-listings">
              {myListings.length > 0 ? myListings.map(listing => (
                <div key={listing.id} className="bg-[#0a0a0a] border border-white/5 p-5 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-bold">{listing.service_name}</h3>
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 uppercase ${listing.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-muted-foreground'}`}>{listing.status}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{listing.outcome_statement}</p>
                    <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                      <span>${listing.price_monthly}/mo</span>
                      <span>{listing.total_inquiries} inquiries</span>
                      <span>Quality: {listing.quality_scores?.overall}%</span>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
                  <p className="text-muted-foreground mb-4">No listings yet. Publish a validated workflow to get started.</p>
                  <Button onClick={() => setShowPublishModal(true)} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm">Publish Workflow</Button>
                </div>
              )}
            </div>
          )}

          {tab === 'inquiries' && (
            <div className="space-y-3" data-testid="inquiries-list">
              {inquiries.length > 0 ? inquiries.map(inq => (
                <div key={inq.id} className="bg-[#0a0a0a] border border-white/5 p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-bold">{inq.company_name}</h3>
                      <p className="text-sm text-muted-foreground">{inq.contact_name} · {inq.contact_email}</p>
                    </div>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 uppercase ${getStatusStyle(inq.status)}`}>{inq.status}</span>
                  </div>
                  <p className="text-sm bg-black/30 border border-white/5 p-3 mb-3">{inq.message}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{inq.listing_snapshot?.service_name} · {inq.budget_range || 'No budget specified'}</span>
                    <div className="flex gap-2">
                      {inq.status === 'new' && (
                        <>
                          <Button onClick={() => handleInquiryStatus(inq.id, 'responded')} size="sm" className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-7 text-xs" data-testid={`respond-${inq.id}`}>Respond</Button>
                          <Button onClick={() => handleInquiryStatus(inq.id, 'declined')} size="sm" variant="outline" className="border-red-500/20 text-red-400 rounded-sm h-7 text-xs">Decline</Button>
                        </>
                      )}
                      {inq.status === 'responded' && (
                        <Button onClick={() => handleInquiryStatus(inq.id, 'converted')} size="sm" className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-7 text-xs">Mark Converted</Button>
                      )}
                    </div>
                  </div>
                </div>
              )) : (
                <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
                  <p className="text-muted-foreground">No inquiries yet. Publish a listing to start receiving leads.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Inquiry Modal */}
      <Dialog open={showInquiryModal} onOpenChange={setShowInquiryModal}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md">
          <DialogHeader><DialogTitle>Inquire: {selectedListing?.service_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Company Name *</label>
              <Input value={inquiryForm.company_name} onChange={e => setInquiryForm({ ...inquiryForm, company_name: e.target.value })} className="bg-black/50 border-white/10 rounded-sm" placeholder="Your Company" data-testid="inquiry-company" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase mb-2 block">Your Name</label>
                <Input value={inquiryForm.contact_name} onChange={e => setInquiryForm({ ...inquiryForm, contact_name: e.target.value })} className="bg-black/50 border-white/10 rounded-sm" placeholder="John Smith" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase mb-2 block">Email *</label>
                <Input type="email" value={inquiryForm.contact_email} onChange={e => setInquiryForm({ ...inquiryForm, contact_email: e.target.value })} className="bg-black/50 border-white/10 rounded-sm" placeholder="john@company.com" data-testid="inquiry-email" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Message *</label>
              <Textarea value={inquiryForm.message} onChange={e => setInquiryForm({ ...inquiryForm, message: e.target.value })} className="bg-black/50 border-white/10 rounded-sm min-h-[80px]" placeholder="Tell us about your needs..." data-testid="inquiry-message" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Budget Range</label>
              <Input value={inquiryForm.budget_range} onChange={e => setInquiryForm({ ...inquiryForm, budget_range: e.target.value })} className="bg-black/50 border-white/10 rounded-sm" placeholder="$500-1000/mo" />
            </div>
            <Button onClick={handleInquiry} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="send-inquiry-btn">Send Inquiry</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish Modal */}
      <Dialog open={showPublishModal} onOpenChange={setShowPublishModal}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg">
          <DialogHeader><DialogTitle>Publish to Marketplace</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {workflows.length === 0 ? (
              <div className="text-center py-6">
                <Shield className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="font-bold mb-1">No eligible workflows</p>
                <p className="text-sm text-muted-foreground mb-4">You need a workflow with ≥90% completeness and ≥75% automation to publish.</p>
                <Button onClick={() => { setShowPublishModal(false); navigate('/workflow'); }} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm">Build Workflow</Button>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs text-muted-foreground uppercase mb-2 block">Select Workflow</label>
                  <Select value={publishForm.workflow_id} onValueChange={v => setPublishForm({ ...publishForm, workflow_id: v })}>
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm"><SelectValue placeholder="Choose workflow" /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10">
                      {workflows.map(w => <SelectItem key={w.id} value={w.id}>{w.name} ({w.completeness_score}%)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase mb-2 block">Service Name</label>
                  <Input value={publishForm.service_name} onChange={e => setPublishForm({ ...publishForm, service_name: e.target.value })} className="bg-black/50 border-white/10 rounded-sm" placeholder="AI Patient Scheduling" data-testid="publish-name" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase mb-2 block">Outcome Statement</label>
                  <Input value={publishForm.outcome_statement} onChange={e => setPublishForm({ ...publishForm, outcome_statement: e.target.value })} className="bg-black/50 border-white/10 rounded-sm" placeholder="Reduce scheduling overhead by 80%" data-testid="publish-outcome" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase mb-2 block">Description</label>
                  <Textarea value={publishForm.service_description} onChange={e => setPublishForm({ ...publishForm, service_description: e.target.value })} className="bg-black/50 border-white/10 rounded-sm min-h-[60px]" placeholder="Describe your service..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase mb-2 block">Price ($/mo)</label>
                    <Input type="number" value={publishForm.price_monthly} onChange={e => setPublishForm({ ...publishForm, price_monthly: parseFloat(e.target.value) || 0 })} className="bg-black/50 border-white/10 rounded-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase mb-2 block">Max Clients</label>
                    <Input type="number" value={publishForm.max_clients} onChange={e => setPublishForm({ ...publishForm, max_clients: parseInt(e.target.value) || 10 })} className="bg-black/50 border-white/10 rounded-sm" />
                  </div>
                </div>
                <Button onClick={handlePublish} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="publish-submit">Publish Service</Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Marketplace;
